import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { fuzzyMatch } from "../lib/fuzzySearch";
import { HelpBubble } from "../components/HelpBubble";
import {
  Plus, Edit, Trash2, BookOpen, Search, Loader2, AlertCircle, Sparkles, BarChart3,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ClauseEditorDialog, clauseCategories } from "../components/clauses/ClauseEditorDialog";
import { AIDraftClauseDialog } from "../components/clauses/AIDraftClauseDialog";
import { BenchmarkDialog } from "../components/clauses/BenchmarkDialog";

const EMPTY_FORM = { name: "", category: "", description: "", content: "", is_mandatory: false };

export default function ClausesPage() {
  const { hasFeature, canDo } = useAuth();
  const [clauses, setClauses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClause, setSelectedClause] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);

  const [benchmarkDialogOpen, setBenchmarkDialogOpen] = useState(false);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const [benchmarkResult, setBenchmarkResult] = useState(null);
  const [benchmarkClause, setBenchmarkClause] = useState(null);

  const canManage = canDo("clauses.create_edit_delete");

  const fetchClauses = useCallback(async () => {
    try {
      const params = categoryFilter !== "all" ? `?category=${categoryFilter}` : "";
      const response = await apiClient.get(`/clauses${params}`);
      setClauses(response.data);
    } catch { toast.error("Failed to load clauses"); }
    finally { setLoading(false); }
  }, [categoryFilter]);

  useEffect(() => { fetchClauses(); }, [fetchClauses]);

  const filteredClauses = useMemo(() =>
    clauses.filter(c => !searchQuery || fuzzyMatch(searchQuery, [c.name, c.category, c.content])),
    [clauses, searchQuery]
  );

  // ── Clause CRUD ──
  const resetForm = () => { setSelectedClause(null); setFormData(EMPTY_FORM); };

  const openEditDialog = (clause) => {
    setSelectedClause(clause);
    setFormData({ name: clause.name, category: clause.category, description: clause.description || "", content: clause.content, is_mandatory: clause.is_mandatory });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.category || !formData.content) { toast.error("Please fill in all required fields"); return; }
    setSaving(true);
    try {
      if (selectedClause) { await apiClient.put(`/clauses/${selectedClause.id}`, formData); toast.success("Clause updated"); }
      else { await apiClient.post("/clauses", formData); toast.success("Clause created"); }
      setDialogOpen(false); resetForm(); fetchClauses();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save clause"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedClause) return;
    try { await apiClient.delete(`/clauses/${selectedClause.id}`); toast.success("Clause deleted"); setDeleteDialogOpen(false); setSelectedClause(null); fetchClauses(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete clause"); }
  };

  // ── AI Draft callbacks ──
  const handleGenerateDraft = async (purpose, jurisdiction, contractType, feedback) => {
    if (!purpose.trim()) { toast.error("Describe what the clause should cover"); return null; }
    setAiDrafting(true);
    try {
      const purposeWithFeedback = feedback ? `${purpose}\n\nIMPORTANT REFINEMENT INSTRUCTIONS:\n${feedback}` : purpose;
      const res = await apiClient.post("/ai/draft-clause", { purpose: purposeWithFeedback, jurisdiction, contract_type: contractType });
      return res.data;
    } catch (err) { toast.error(err.response?.data?.detail || "AI drafting failed"); return null; }
    finally { setAiDrafting(false); }
  };

  const handleAcceptDraft = async (draft) => {
    if (!draft) return;
    setSaving(true);
    try {
      await apiClient.post("/clauses", {
        name: draft.clause_name || draft.name || "Untitled Clause",
        category: draft.category || "General", description: draft.description || "",
        content: draft.content || "", is_mandatory: draft.is_mandatory || false,
      });
      toast.success("Clause saved to library"); setAiDialogOpen(false); fetchClauses();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save clause"); }
    finally { setSaving(false); }
  };

  const handleEditDraft = (draft) => {
    setFormData({
      name: draft.clause_name || draft.name || "", category: draft.category || "",
      description: draft.description || "", content: draft.content || "",
      is_mandatory: draft.is_mandatory || false,
    });
    setSelectedClause(null); setAiDialogOpen(false); setDialogOpen(true);
  };

  // ── Benchmark ──
  const handleBenchmark = async (clause) => {
    setBenchmarkClause(clause); setBenchmarkResult(null); setBenchmarkDialogOpen(true); setBenchmarkLoading(true);
    try { const res = await apiClient.post(`/clauses/${clause.id}/benchmark`); setBenchmarkResult(res.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Benchmarking failed"); setBenchmarkDialogOpen(false); }
    finally { setBenchmarkLoading(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clauses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Clause Library <HelpBubble tip="Reusable legal clauses for contract drafting. Use the benchmark button to score any clause against industry best practices." />
          </h1>
          <p className="text-slate-500 mt-1">Pre-approved clauses for contract drafting</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            {hasFeature("ai_clause_generation") && (
              <Button onClick={() => setAiDialogOpen(true)} variant="outline" className="gap-2 rounded-sm border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="ai-draft-clause-btn">
                <Sparkles size={16} /> AI Draft Clause
              </Button>
            )}
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="gap-2 rounded-sm bg-slate-900 hover:bg-slate-800 dark:bg-cyan-500 dark:text-slate-900" data-testid="create-clause-btn">
              <Plus size={18} /> New Clause
            </Button>
          </div>
        )}
      </div>

      {/* Search & Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input placeholder="Search clauses..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-sm" data-testid="search-clauses-input" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="clause-category-filter"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {clauseCategories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Clauses List */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={32} /></div>
      ) : filteredClauses.length > 0 ? (
        <div className="space-y-4">
          {filteredClauses.map(clause => (
            <Card key={clause.id} className="hover:shadow-md transition-shadow" data-testid={`clause-card-${clause.id}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <BookOpen size={18} className="text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-slate-900 dark:text-white">{clause.name}</h3>
                        {clause.is_mandatory && <Badge className="bg-red-100 text-red-700 rounded-sm"><AlertCircle size={12} className="mr-1" />Mandatory</Badge>}
                      </div>
                      <Badge variant="outline" className="text-xs mb-2">{clause.category}</Badge>
                      <p className="text-sm text-slate-500 line-clamp-2">{clause.content}</p>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-cyan-600 hover:text-cyan-700" onClick={() => handleBenchmark(clause)} title="Benchmark" data-testid={`benchmark-clause-${clause.id}`}><BarChart3 size={14} /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(clause)} data-testid={`edit-clause-${clause.id}`}><Edit size={14} /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => { setSelectedClause(clause); setDeleteDialogOpen(true); }} data-testid={`delete-clause-${clause.id}`}><Trash2 size={14} /></Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-20">
          <BookOpen size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500 mb-4">No clauses found</p>
          {canManage && <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="gap-2 rounded-sm"><Plus size={18} /> Create Clause</Button>}
        </div>
      )}

      {/* Extracted Dialog Components */}
      <ClauseEditorDialog open={dialogOpen} onOpenChange={setDialogOpen} selectedClause={selectedClause} formData={formData} setFormData={setFormData} onSave={handleSave} saving={saving} />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Clause</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{selectedClause?.name}"? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="confirm-delete-clause-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AIDraftClauseDialog open={aiDialogOpen} onOpenChange={setAiDialogOpen} onGenerateDraft={handleGenerateDraft} onAcceptDraft={handleAcceptDraft} onEditDraft={handleEditDraft} onDiscard={() => setAiDialogOpen(false)} aiDrafting={aiDrafting} saving={saving} />

      <BenchmarkDialog open={benchmarkDialogOpen} onOpenChange={setBenchmarkDialogOpen} loading={benchmarkLoading} result={benchmarkResult} clause={benchmarkClause} />
    </div>
  );
}
